import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Descriptions,
  Spin,
  Table,
  Tag,
  Tooltip,
  message,
} from 'antd';
import { InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import PageHeaderAction from '../../../../components/PageHeaderAction';
import TrackingChain from '../../../../components/TrackingChain';
import AttachmentUpload from '../../../../components/AttachmentUpload';
import DeliveryReceiptForm from '../DeliveryReceiptForm';
import LineDrawer from './LineDrawer';
import {
  useDeliveryReceiptStore,
  useFetchData,
  useFetchFile,
  useUpdateData,
} from '../../../../hooks';
import {
  DRS_API,
  useUpdateDeliveryReceiptStatus,
} from '../../../../hooks/useDeliveryReceiptHooks';
import { useAuthStore, useDrawerStore } from '../../../../store';
import { BaseModel, PageableResponse } from '../../../../types';
import {
  DeliveryLineRecord,
  DeliveryReceiptRecord,
} from '../../../../types/DeliveryReceipt';
import { Resource } from '../../../../lib/enums';
import {
  convertDate,
  dateFormat,
  isModuleApproved,
  isUserCanEdit,
} from '../../../../lib/utils';

const statusColour: Record<string, string> = {
  DRAFT: 'default',
  CONFIRM: 'processing',
  APPROVED: 'success',
  DONE: 'success',
  CANCEL: 'error',
};

/**
 * One delivery receipt: what went out, to whom, and who carried it.
 *
 * A receipt raised from a receiving report arrives with its lines already
 * filled but no destination -- the receipt cannot know who asked for the
 * material -- so that is what this screen exists to supply.
 */
const DeliveryReceiptInfoPage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const drawerStore = useDrawerStore();
  const drStore = useDeliveryReceiptStore();
  const canEdit = isUserCanEdit(user?.moduleAccess, 'Internal DR');

  const [lineDrawer, setLineDrawer] = useState(false);
  const [editingLine, setEditingLine] = useState<DeliveryLineRecord | null>(
    null,
  );

  const { data, isLoading } = useFetchData<DeliveryReceiptRecord>(
    `${DRS_API}/${params.id}`,
  );
  const { data: files } = useFetchFile(
    `${DRS_API}/${params.id}/files`,
    'api/stock/drs',
  );
  const { data: productData } = useFetchData<PageableResponse<BaseModel>>(
    '/product/products',
    { resource: Resource.NAME, length: 999999 },
  );
  const { data: uomData } = useFetchData<PageableResponse<BaseModel>>(
    '/product/uoms',
    { resource: Resource.NAME, length: 999999 },
  );

  const { mutate: update } = useUpdateData(DRS_API);

  const lines = data?.movements ?? [];
  const delivered = data?.status === 'DONE';
  const submitted = data?.status === 'CONFIRM';
  const approved = data?.status === 'APPROVED';
  const canApprove =
    user?.role === 'ROLE_ADMIN' ||
    isModuleApproved(user?.moduleAccess, 'Internal DR');
  const { mutate: setStatus, isPending: changingStatus } =
    useUpdateDeliveryReceiptStatus(params.id);

  /**
   * One handler for both steps. The API decides whether a given status is a
   * submission or a decision, and refuses a decision made by whoever submitted
   * it -- so the buttons here only have to offer the step, not police it.
   */
  const move = (status: string, ok: string) =>
    setStatus(
      { status },
      {
        onSuccess: () => message.success(ok),
        onError: (e: any) =>
          message.error(
            e?.response?.data?.message ?? 'Could not update this receipt.',
          ),
      },
    );
  const noDestination = !data?.destination;

  const saveLines = (next: DeliveryLineRecord[]) => {
    if (!data?.id) return;
    update(
      { id: data.id, form: { ...data, movements: next } },
      {
        onSuccess: () => message.success('Delivery receipt updated'),
        onError: (e: any) =>
          message.error(
            e?.response?.data?.message ??
              'Could not save the delivery receipt.',
          ),
      },
    );
  };

  const columns = [
    {
      title: 'Material',
      dataIndex: 'description',
      render: (d: string, row: DeliveryLineRecord) =>
        d || row.product?.description || '—',
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      width: 160,
      render: (q: number, row: DeliveryLineRecord) => (
        <span>
          {q ?? '—'}{' '}
          <span className={'text-gray-400'}>
            {row.productUom?.description ?? ''}
          </span>
        </span>
      ),
    },
    ...(canEdit && !delivered
      ? [
          {
            title: 'Action',
            key: 'action',
            width: 140,
            render: (_: unknown, row: DeliveryLineRecord) => (
              <div className={'flex gap-3'}>
                <a
                  onClick={() => {
                    setEditingLine(row);
                    setLineDrawer(true);
                  }}
                >
                  Edit
                </a>
                <a
                  onClick={() =>
                    saveLines(lines.filter((l) => l.id !== row.id))
                  }
                >
                  Remove
                </a>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <Spin spinning={isLoading}>
      <div className={'page-wrapper'}>
        <div className={'flex items-center justify-between'}>
          <div>
            <PageHeaderAction
              moduleName={`Internal DR ${data?.code ?? ''}`}
              action={null}
            />
            {/*
              Two different documents are called a DR here. This is ours --
              material going out from MMD to the requestor, steps 13 and 22 of
              the sourcing flow. The supplier's own delivery receipt is a
              number recorded on a Receiving Report, not this.
            */}
            <Tooltip title="Material going out from MMD to the requestor. A supplier's own delivery receipt is recorded as a number on the Receiving Report instead.">
              <span className={'text-gray-400 text-xs cursor-help'}>
                Outbound to the requestor <InfoCircleOutlined />
              </span>
            </Tooltip>
          </div>
          <div className={'flex gap-2'}>
            {canEdit && !delivered && !submitted && !approved && (
              <Button
                type={'primary'}
                loading={changingStatus}
                disabled={lines.length === 0}
                onClick={() => move('CONFIRM', 'Sent for approval.')}
              >
                Submit for approval
              </Button>
            )}
            {canApprove && submitted && (
              <Button
                type={'primary'}
                loading={changingStatus}
                onClick={() => move('APPROVED', 'Approved.')}
              >
                Approve
              </Button>
            )}
            {canApprove && submitted && (
              <Button
                danger
                loading={changingStatus}
                onClick={() => move('DRAFT', 'Sent back to the preparer.')}
              >
                Return
              </Button>
            )}
            {canApprove && approved && (
              <Button
                type={'primary'}
                loading={changingStatus}
                onClick={() => move('DONE', 'Marked as delivered.')}
              >
                Mark delivered
              </Button>
            )}
            {canEdit && !delivered && (
              <Button
                onClick={() => {
                  drStore.setData(data ?? null);
                  drawerStore.onClose();
                }}
              >
                Edit
              </Button>
            )}
            <Button
              onClick={() => navigate('/stock-movement/delivery-receipt')}
            >
              Back
            </Button>
          </div>
        </div>

        {noDestination && (
          <Alert
            type={'warning'}
            showIcon
            className={'mb-4'}
            message={'No destination set'}
            description={
              'A receipt raised from a receiving report does not know who asked for the material. Use Edit to say where it is going before it leaves.'
            }
          />
        )}

        <Descriptions bordered size={'small'} column={3} className={'mb-4'}>
          <Descriptions.Item label={'DR No.'}>{data?.code}</Descriptions.Item>
          <Descriptions.Item label={'Date'}>
            {data?.requiredDate
              ? convertDate(data.requiredDate, dateFormat)
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label={'Status'}>
            <Tag color={statusColour[data?.status ?? 'DRAFT']}>
              {data?.status}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={'Deliver To'}>
            {data?.destination?.description ?? (
              <span className={'text-amber-600'}>not set</span>
            )}
          </Descriptions.Item>
          <Descriptions.Item label={'From'}>
            {data?.location?.description ?? '—'}
          </Descriptions.Item>
          <Descriptions.Item label={'Carried By'}>
            {data?.remarks || '—'}
          </Descriptions.Item>
          <Descriptions.Item label={'Source'} span={3}>
            {data?.source || '—'}
          </Descriptions.Item>
          <Descriptions.Item label={'Notes'} span={3}>
            {data?.notes || '—'}
          </Descriptions.Item>
        </Descriptions>

        <div className={'mb-4'}>
          <TrackingChain api={DRS_API} id={params.id} />
        </div>

        <div className={'flex items-center justify-between py-2'}>
          <span className={'text-base font-semibold'}>
            Items · {lines.length}
          </span>
          {canEdit && !delivered && (
            <Button
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingLine(null);
                setLineDrawer(true);
              }}
            >
              Add Item
            </Button>
          )}
        </div>

        <Table<DeliveryLineRecord>
          rowKey={(r) => r.id ?? Math.random().toString()}
          dataSource={lines}
          columns={columns}
          size={'small'}
          pagination={lines.length > 25 ? { pageSize: 25 } : false}
        />

        <div className={'py-4'}>
          <AttachmentUpload
            label={'Signed Internal DR'}
            attachments={files}
            api={DRS_API}
            recordId={params.id}
            tag={'delivery'}
            canEdit={canEdit}
          />
        </div>

        <DeliveryReceiptForm api={DRS_API} moduleName={'Internal DR'} />

        <LineDrawer
          open={lineDrawer}
          line={editingLine}
          products={productData?.data ?? []}
          uoms={uomData?.data ?? []}
          onClose={() => {
            setLineDrawer(false);
            setEditingLine(null);
          }}
          onSave={(line) => {
            const next = line.id
              ? lines.map((l) => (l.id === line.id ? line : l))
              : [...lines, line];
            saveLines(next);
            setLineDrawer(false);
            setEditingLine(null);
          }}
        />
      </div>
    </Spin>
  );
};

export default DeliveryReceiptInfoPage;
